/** Pre-populated sample-session trigger — Phase 3-demo.
 *
 * One click → backend seeds (or returns existing) demo session →
 * navigate to /sessions/:id so the prospect immediately sees a
 * populated SessionPage with photos, annotations, whiteboard, audit
 * chain. Idempotent: clicking twice routes to the same demo session.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ApiPaths, api } from "@/lib/api";
import type { BillingMeResponse } from "@/lib/types";


type SeedResponse = {
  session_id: string;
  evidence_count: number;
  annotation_count: number;
  redirect_to: string;
};


export function TrySampleButton() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // The sample inspection is an onboarding aid for free/trial orgs — hide
  // it once the org is on a paid plan. (Shared "billing-me" query key, so
  // it dedupes with the billing panel/banner.)
  const billing = useQuery({
    queryKey: ["billing-me"],
    queryFn: async () =>
      (await api.get<BillingMeResponse>(ApiPaths.billingMe)).data,
    retry: false,
  });

  const seed = useMutation({
    mutationFn: async () => {
      const res = await api.post<SeedResponse>(ApiPaths.demoSeed);
      return res.data;
    },
    onSuccess: (data) => {
      navigate(data.redirect_to);
    },
  });

  // Confirmed paid plan → don't show the sample CTA. Unknown plan
  // (loading / no billing.read) → show, as before.
  if (billing.data && billing.data.subscription.plan.slug !== "free") {
    return null;
  }

  return (
    <button
      type="button"
      className="btn-ghost text-sm"
      onClick={() => seed.mutate()}
      disabled={seed.isPending}
      data-testid="try-sample"
      title={t("dashboard.sampleHint")}
    >
      {seed.isPending
        ? t("dashboard.samplePreparing")
        : t("dashboard.trySample")}
    </button>
  );
}
